/** The bar that replaces the filter line while any tick-box is ticked: "3 selected | actions | Clear".
 *
 * **The page supplies the actions** (`GridPower.bulkActions`). This component invents none: the only
 * one it draws itself is Export selected as CSV, which reads the grid's own cells. A page offers an
 * action only to a role that may take it (design rule 6: no control that predictably fails), so a
 * Viewer's bar has the export and Clear and nothing else.
 *
 * Escape clears the selection (the grid handles it for the table; the bar does it for focus that has
 * moved onto the bar's own buttons).
 */

import { DownloadSimple, X } from "@phosphor-icons/react";
import type { KeyboardEvent } from "react";

import { quantity } from "../../../lib/format";
import styles from "./tablePower.module.css";
import type { GridPower } from "./useGridPower";

export function BulkBar({ power }: { power: GridPower }) {
  const count = power.selectedCount;
  const onKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      power.clearSelection();
    }
  };
  return (
    <div
      className={styles.bulk}
      role="toolbar"
      aria-label="Bulk actions"
      data-testid="bulk-bar"
      data-filter-line-replaced
      onKeyDown={onKey}
    >
      <span className={styles.bulkCount} role="status" data-testid="bulk-count">
        {quantity(String(count))} selected
      </span>
      <span className={styles.sep} aria-hidden="true" />
      <div className={styles.bulkActions}>
        {power.bulkActions?.({
          keys: [...power.selected],
          count,
          allMatching: power.allMatching,
        })}
        <button
          type="button"
          className={styles.tool}
          data-testid="bulk-export"
          onClick={() => power.exportCsv(true)}
        >
          <DownloadSimple size={14} aria-hidden="true" />
          Export selected as CSV
        </button>
      </div>
      <button type="button" className={styles.clearBulk} data-testid="bulk-clear" onClick={power.clearSelection}>
        <X size={12} weight="bold" aria-hidden="true" />
        Clear
      </button>
    </div>
  );
}
